/**
 * Analytics Utilities
 * 
 * Wrapper for analytics events that works with both web and plugin environments.
 * Provides fallback behavior when analytics service is unavailable.
 */

import { getPlatformSource } from './environment';

/**
 * Event Type Enum
 * Matches the original eventType from MixPanelEvents
 */
export enum EventType {
  signUp = 1,
  signIn,
  logOut,
  startSessions,
  responsiveDesignClicks,
  nonResponsiveDesignClicks,
  localizeYourWorkFeatureClicks,
  cancelExportResponsiveDesign,
  cancelExportNonResponsiveDesign,
  cancelLocalizeYourWorkFeature,
  completeResponsiveDesign,
  completeNonResponsiveDesign,
  completeLocalizeYourWorkFeature,
  feedback,
  ideozDSClicks,
  templateButtonClicks,
  documentationView,
  videoView,
  articleView,
  hint,
  AI_click,
  AI_quickly_generate,
  AI_manual_prompt,
  AI_mobile_layout_click,
  AI_desktop_layout_click,
  AI_mobile_layout_start,
  AI_desktop_layout_start,
  AI_mobile_layout_cancel,
  AI_desktop_layout_cancel,
  AI_mobile_layout_failed,
  AI_desktop_layout_failed,
  AI_mobile_layout_succeeded,
  AI_desktop_layout_succeeded,
  AI_mobile_layout_time,
  AI_desktop_layout_time,
  AI_complete_layout,
  AI_try_again,
  duration,
  visit,
  clickOnTranslatePage = 40,
  clickOnTemplatesPage,
  clickOnCodePage,
  clickOnGeneratePage,
  createGenericConversationFromHomePage,
  createGenericConversationFromHistory,
  sendMessageGenericConversation,
  responseFromGenericBot,
  openConversationGenericFromHistory,
  openAiFormClick,
  answerOnAiFormQuestion,
  openAiChatConversation,
  sendMessageAiChatConversation,
  responseFromAiBot,
  generateAi,
  cancelAi,
  clickGenerateAiFromHistory,
  completeGenerateAi,
  generateAiRate,
  showSwitchFileType,
  requestUpgrade,
  exportResponsiveDesign,
  exportNonResponsiveDesign,
  translateDesign,
  translateError,
  // 🔹 Authentication
  ClickOnRegister,
  ClickOnLogin,
  RedirectedToWebsite,
  ReturnAndLinkAccount,
  // 🔹 Profile
  ClickOnProfilePage,
  ClickOnChangePassword,
  ClickOnLogout,
  // 🔹 Sidebar / Navigation
  ClickOnHelpDocumentation,
  ClickOnWebsite,
  ClickOnShare,
  ClickOnSocialLinks,
  // 🔹 Onboarding / First Time
  ClickOnWhatIsIdeozProject,
  ClickOnCreateProjectAfterVideo,
  ClickOnCreateAccountAfterVideo,
  OnSendFirstMessage,
  ClickOnHelperPrompt,
  OnModelReply,
  OnUserMessage,
  // 🔹 Project & Account
  ClickOnCreateProject,
  ClickOnCreateAccount,
  OnLeaveChat,
  OnClosePopupCreatingAccount,
  OnClosePopupCreatingProjectAndLeaveChat,
  OnClosePopupCreatingProject,
  // 🔹 Project Updates
  AfterFilesUploaded,
  AfterUpdatingProjectDetails,
  // 🔹 Delete Project
  ClickOnDeleteProjectButton,
  ConfirmDeleteProject,
  ClickOnExitBeforeCompleteDeleteProject,
  // 🔹 Repeating / Chat Scenarios
  OnChatRepetition,
  ClickOnReframeIt,
  ClickOnSkip,
  // 🔹 Generation / Retry
  ClickOnTryAgain,
  ClickOnGenerateHere,
  ClickOnFigjam,
  // 🔹 File Upload
  UserUploadLargeFiles,
  UserUploadNotSupportedFiles,
  // 🔹 Chat Banner
  VisibleChatBanner,
}

/**
 * Analytics event data interface
 */
export interface AnalyticsEventData {
  [key: string]: any;
}

/**
 * Analytics service callback type
 * This should be provided by the parent application
 */
export type AnalyticsCallback = (
  eventTypeId: number,
  eventData?: AnalyticsEventData
) => Promise<any>;

/**
 * Global analytics callback - set by the parent application
 */
let analyticsCallback: AnalyticsCallback | null = null;

/**
 * Set the analytics callback function
 * This should be called by the parent application during initialization
 */
export const setAnalyticsCallback = (callback: AnalyticsCallback): void => {
  analyticsCallback = callback;
};

/**
 * Add a new analytics event
 * 
 * @param eventEnum - Event type from EventType enum
 * @param eventData - Additional event data
 * @returns Promise that resolves when event is sent
 */
export const addNewEvent = async (
  eventEnum: number,
  eventData?: AnalyticsEventData
): Promise<any> => {
  const platformSource = getPlatformSource();
  const eventPayload = {
    event_type_id: eventEnum,
    source: platformSource,
    event_data: { ...eventData },
  };

  // If analytics callback is set, use it
  if (analyticsCallback) {
    try {
      return await analyticsCallback(eventEnum, eventData);
    } catch (error) {
      console.warn('Analytics event failed:', error);
      return null;
    }
  }

  // Fallback: just log the event
  // @ts-ignore - process.env may not be available in all environments
  if (typeof process !== 'undefined' && process?.env?.NODE_ENV === 'development') {
    console.log('📊 Analytics Event:', eventPayload);
  }

  return null;
};

/**
 * Add a simple event without extra data
 * 
 * @param eventEnum - Event type from EventType enum
 * @returns Promise that resolves when event is sent
 */
export const addEvent = async (eventEnum: number): Promise<any> => {
  return addNewEvent(eventEnum);
};

// Re-export for backward compatibility
export { EventType as eventType };
